import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Notification } from '../../models/notification/notificaiton.model';
import { AlertType } from '../../components/alert/alert';

@Injectable({
  providedIn: 'root'
})
export class NotificationHubService {
  private hubConnection: signalR.HubConnection | null = null;
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private isConnectedSubject = new BehaviorSubject<boolean>(false);

  public notifications$: Observable<Notification[]> = this.notificationsSubject.asObservable();
  public isConnected$: Observable<boolean> = this.isConnectedSubject.asObservable();

  constructor() {}

  public startConnection(employeeGuid: string): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalnotificationHubUrl, {
        withCredentials: true,
        // Long polling occupies Safari's per-host HTTP slots and can stall login APIs on mobile.
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection.on('ReceiveNotification', (notification: Notification) => {
      console.log('New notification:', notification);
      const current = this.notificationsSubject.value;
      this.notificationsSubject.next([notification, ...current]);
    });

    this.hubConnection.on('SubscriptionConfirmed', (data) => {
      console.log('Subscription confirmed:', data);
    });

    this.hubConnection.onreconnected(() => {
      console.log('Reconnected');
      this.isConnectedSubject.next(true);
      this.subscribeToUser(employeeGuid);
    });

    this.hubConnection.onclose(() => {
      console.log('Connection closed');
      this.isConnectedSubject.next(false);
    });

    this.hubConnection
      .start()
      .then(() => {
        console.log('Connected to NotificationHub');
        this.isConnectedSubject.next(true);
        this.subscribeToUser(employeeGuid);
      })
      .catch(err => {
        console.error('Error connecting:', err);
        this.isConnectedSubject.next(false);
      });
  }

  public subscribeToUser(employeeGuid: string): void {
    this.hubConnection?.invoke('SubscribeToUser', employeeGuid)
      .catch(err => console.error('Error subscribing:', err));
  }

  public markAsRead(notificationId: string): void {
    this.hubConnection?.invoke('MarkAsRead', notificationId)
      .then(() => {
        const current = this.notificationsSubject.value;
        const updated = current.map(n => 
          n.id === notificationId ? { ...n, isRead: true } : n
        );
        this.notificationsSubject.next(updated);
      })
      .catch(err => console.error('Error marking as read:', err));
  }

  public markAllRead(): void {
    const current = this.notificationsSubject.value;
    const updated = current.map(n => ({ ...n, isRead: true }));
    this.notificationsSubject.next(updated);
    
    // Optional: notify backend about marking all as read
    // this.hubConnection?.invoke('MarkAllRead')?.catch(err => console.error('Error notifying backend:', err));
  }

  public clearAll(): void {
    this.notificationsSubject.next([]);
    
    // Optional: notify backend about clearing all
    // this.hubConnection?.invoke('ClearAll')?.catch(err => console.error('Error notifying backend:', err));
  }

  public sendTestNotification(): void {
    this.hubConnection?.invoke('SendTestNotification')
      .catch(err => console.error('Error sending test:', err));
  }

  /**
   * Add a local notification to the notification panel (not from SignalR).
   * This centralizes all application messages/notifications in the notification panel.
   * 
   * @param type - The type of notification: 'success', 'error', 'warning', 'info'
   * @param subject - The notification title/summary
   * @param body - The notification detail message
   */
  public addLocalNotification(type: AlertType, subject: string, body: string): void {
    const notification: Notification = {
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: type,
      subject: subject,
      body: body,
      createdAt: new Date().toISOString(),
      isRead: false
    };
    
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next([notification, ...current]);
  }

  public stopConnection(): void {
    this.hubConnection?.stop()
      .then(() => {
        this.isConnectedSubject.next(false);
      })
      .catch(err => console.error('Error stopping connection:', err));
  }
}